import React from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import useCareerDetail from "../Hooks/useCareerDetail";
import NavbarWithPushContent from "./NavbarWithPushContent";
import LoadingSpinner from "../Assets/LoadingSpinner";
import { FaArrowLeft } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Seo, { SITE_URL } from "../Assets/Seo";

const stripHtml = (html?: string) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || "").replace(/\s+/g, " ").trim();
};

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};

interface CareerDetailProps {
  products: ProductCategory[];
  lang: string
}

const CareerDetail: React.FC<CareerDetailProps> = ({ products, lang }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { career, loading, error } = useCareerDetail(id);

  const department =
    career?.department && typeof career.department === "object"
      ? (career.department as { [key: string]: string })[lang]
      : "";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang} />
      </div>

      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      <section className="bg-gray-50 px-4 pt-28 md:pt-32 pb-16">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <p className="text-center text-red-500 text-lg">{error}</p>
          ) : career ? (
            <>
              <Seo
                title={career.vacancy?.[lang]}
                description={stripHtml(
                  (career.shortDescription as { [key: string]: string })?.[lang] ||
                    career.description?.[lang]
                ).slice(0, 160)}
                type="article"
                lang={lang}
                jsonLd={{
                  "@context": "https://schema.org",
                  "@type": "JobPosting",
                  title: career.vacancy?.[lang],
                  description: career.description?.[lang],
                  datePosted: career.createdAt,
                  employmentType: "FULL_TIME",
                  hiringOrganization: {
                    "@type": "Organization",
                    name: "Galafarm",
                    sameAs: SITE_URL,
                    logo: `${SITE_URL}/GalafarmLogo.png`,
                  },
                  jobLocation: {
                    "@type": "Place",
                    address: {
                      "@type": "PostalAddress",
                      addressLocality: "Skopje",
                      addressCountry: "MK",
                    },
                  },
                }}
              />
              <Link
                to="/careers"
                className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-600 transition-colors duration-200 hover:text-brand-700"
              >
                <FaArrowLeft className="text-xs" />
                {t("navbar.careers")}
              </Link>

              <article className="rounded-3xl border border-gray-100 bg-white p-6 shadow-md sm:p-10">
                <span className="mb-5 block h-1 w-12 rounded-full bg-brand-400" />
                <h1 className="text-2xl sm:text-4xl font-bold leading-tight text-brand-gray">
                  {career.vacancy[lang]}
                </h1>

                {department && (
                  <span className="mt-4 inline-block rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-600">
                    {department}
                  </span>
                )}

                <div
                  className="prose mt-8 max-w-none text-gray-600"
                  dangerouslySetInnerHTML={{ __html: career.description[lang] }}
                />

                <div className="mt-10 border-t border-gray-100 pt-6">
                  <a
                    href={`mailto:info@galafarm.com.mk?subject=${encodeURIComponent(
                      career.vacancy[lang] || "Application"
                    )}`}
                    className="inline-block rounded-lg bg-brand-400 px-6 py-3 font-semibold text-white transition-colors duration-300 hover:bg-brand-500"
                  >
                    {t("career.applyButton")}
                  </a>
                </div>
              </article>
            </>
          ) : (
            <p className="text-center text-gray-600 text-lg">Career not found.</p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareerDetail;
